import { ArrowUpRight } from "lucide-react";

const products = [
  {
    id: "hunt",
    tag: "01 / Decision Game",
    title: "Hunt Dhurandhar",
    sub: "Spy-thriller corporate decision game",
    body:
      "Seven competing factions. Real pressure. A CSSDPH debrief that surfaces genuine decision-making patterns your dashboards will never catch.",
    image:
      "https://images.unsplash.com/photo-1505274664176-44ccaa7969a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY2luZW1hdGljJTIwc3B5JTIwdGhyaWxsZXIlMjBkb3NzaWVyfGVufDB8fHx8MTc4Mjg4NzQ5OXww&ixlib=rb-4.1.0&q=85",
    meta: ["4–8 teams", "2h / round", "half-day → multi-day"],
    large: true,
  },
  {
    id: "recognition",
    tag: "02 / Recognition Platform",
    title: "Recognition Grand Slam",
    sub: "The Recognition Score Engine",
    body:
      "Five dimensions. Five multipliers. Individual Recognition DNA™ profiles. Continuous recognition replacing year-end appraisal theatre.",
    image:
      "https://images.unsplash.com/photo-1516182715020-07f5d1eeba54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMG5pZ2h0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4Mjg4NzQ5OXww&ixlib=rb-4.1.0&q=85",
    meta: ["5 dimensions", "4 acts", "continuous"],
  },
  {
    id: "crazy",
    tag: "03 / Mass Engagement",
    title: "Crazy Sports",
    sub: "Fun. Competition. Humor. Connection.",
    body:
      "Playful mass-participation formats built for fast laughs, real competition and cross-team bonding. Short, scoreable, festival-ready.",
    image:
      "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBzcG9ydHMlMjBhY3RpdmUlMjBjaW5lbWF0aWN8ZW58MHx8fHwxNzgyODg3NDk5fDA&ixlib=rb-4.1.0&q=85",
    meta: ["4 formats", "mass scale", "lunch → festival"],
  },
];

export default function Products() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-28 lg:py-36 border-b border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#dc2626] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#dc2626]" /> 02 · The Formats
            </div>
            <h2 className="font-display uppercase font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Three flagship
              <br />
              <span className="text-neutral-500">simulations.</span> One engine.
            </h2>
          </div>
          <div className="font-mono text-xs text-neutral-500 max-w-sm">
            Every format is a controlled behavioural experiment. Story, pressure,
            debrief — every time.
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ p }) {
  return (
    <a
      href={`#${p.id}`}
      data-testid={`product-card-${p.id}`}
      className={`group relative overflow-hidden border border-white/10 bg-[#0a0a0a] hover:border-[#dc2626]/50 transition-all duration-500 ${
        p.large ? "col-span-12 lg:col-span-8 lg:row-span-2 min-h-[520px]" : "col-span-12 md:col-span-6 lg:col-span-4 min-h-[380px]"
      }`}
    >
      <div className="absolute inset-0">
        <img
          src={p.image}
          alt={p.title}
          className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
      </div>

      <div className="relative z-10 h-full p-6 md:p-8 flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#dc2626] bg-[#dc2626]/10 border border-[#dc2626]/30 px-2 py-1">
            {p.tag}
          </span>
          <ArrowUpRight className="text-white/40 group-hover:text-[#dc2626] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" size={22} />
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
            {p.sub}
          </div>
          <h3
            className={`font-display font-black uppercase leading-[0.9] tracking-tight ${
              p.large ? "text-5xl md:text-6xl lg:text-7xl" : "text-3xl md:text-4xl"
            }`}
          >
            {p.title}
          </h3>
          <p className="mt-4 text-neutral-400 max-w-lg leading-relaxed">{p.body}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {p.meta.map((m, i) => (
              <span
                key={i}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-300 border border-white/15 px-2.5 py-1"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </a>
  );
}
